import * as L from "../mapbox";
import { ClusterPlugin, createFeature, FeatureLayerEvent, GeoJSONLayer, GeoArrayLayer, LayerAddedCallback } from "./common";
import { Feature, FeatureType } from "./api";

/* tslint:disable:no-var-requires no-require-imports */
const supercluster = require("supercluster");
/* tslint:enable:no-var-requires no-require-imports */

export interface WellGeoJSONLayer extends GeoJSONLayer<L.Marker> {
    // Changes the marker icon.
    setIcon(icon: L.Icon): L.Marker;
}

export interface WellGeoArrayLayer extends GeoArrayLayer<L.Marker> {
    // Changes the marker icon.
    setIcon(icon: L.Icon): L.Marker;
}

export interface WellLayerOptions {
    loadFeaturesByArray?: boolean;
    clusterPlugin?: ClusterPlugin;
}

export class WellLayer {

    constructor(options?: WellLayerOptions) {
        this._loadByArray = options && options.loadFeaturesByArray || false;
        this._clusterPlugin = options && options.clusterPlugin || ClusterPlugin.MarkerCluster;
        this._featureLayerJson = null;
        this._featureLayerArray = null;

        if (this._clusterPlugin === ClusterPlugin.SuperCluster) {
            this._supercluster = supercluster({ radius: 60, extent: 256, maxZoom: 17 });
            this._wellSuperclusterGroup = L.geoJson(null, { pointToLayer: this._createClusterIcon });
        }
    }


    clearClickHandler(): void {
        if (this._featureLayerJson && this._featureLayerJson.hasEventListeners("click")) {
            this._featureLayerJson.removeEventListener("click");
        }
        if (this._featureLayerArray && this._featureLayerArray.hasEventListeners("click")) {
            this._featureLayerArray.removeEventListener("click");
        }
        if (this._wellSuperclusterGroup && this._wellSuperclusterGroup.hasEventListeners("click")) {
            this._wellSuperclusterGroup.removeEventListener("click");
        }
    }


    createLayerAdded(map: L.Map, featureAddCallback?: (f: Feature) => void): (e: FeatureLayerEvent<WellGeoJSONLayer>) => void {
        return (e: FeatureLayerEvent<WellGeoJSONLayer>) => {
            const layer = e.layer;
            const properties = layer.feature.properties;

            if (properties.icon) {
                layer.setIcon(L.divIcon(properties.icon));
            } else {
                // TODO: use Well icon
            }

            if (properties.title) {
                layer.bindPopup("<div class='popup-title'>" + properties.title + "</div>");
            }
            // if there is no title, but an ID for a callback
            else if (properties.id) {
                layer.bindPopup("Loading...");
            }

            if (typeof featureAddCallback === "function") {
                featureAddCallback(createFeature(layer, map, FeatureType.Well));
            }
        };
    }


    /**
     * Gets the wrapper layer that should be added to the map instance.
     *
     * @returns
     *     Clustered wells
     */
    getMarkerClusterGroup(): L.MarkerClusterGroup | L.GeoJSON {
        return this._clusterPlugin === ClusterPlugin.SuperCluster
            ? this._wellSuperclusterGroup
            : this._wellMarkerGroup;
    }


    /**
     * Gets the layer used for interacting with the feature.
     *
     * @returns
     *     The feature layer
     */
    getFeatureLayer() : L.mapbox.FeatureLayerT<WellGeoJSONLayer | L.Marker> {
        return this._loadByArray ? this._featureLayerArray : this._featureLayerJson;
    }


    resetFeatureLayer(): void {
        // Creates new empty well feature layer
        this._featureLayerJson = L.mapbox.featureLayer<WellGeoJSONLayer>();
        this._featureLayerArray = L.mapbox.featureLayer<L.Marker>();
    }


    setFeatureLayer(data: GeoJSON.FeatureCollection<GeoJSON.GeometryObject> | any[], onLayerAdded: LayerAddedCallback, map: L.Map, onClick?: (e: L.LeafletEvent) => void): void {
        this.resetFeatureLayer();
        if (Array.isArray(data)) {
            this._setFeatureLayerArray(data, onLayerAdded, onClick);
        } else {
            this._setFeatureLayerJson(data, onLayerAdded, onClick);
        }
        if (this._clusterPlugin === ClusterPlugin.SuperCluster) {
            this._wellSuperclusterGroup.on("layeradd", onLayerAdded);
            if (onClick) {
                this._wellSuperclusterGroup.on("click", onClick);
            }

            this._adjustClusterView(map);

            map.addEventListener("moveend", (e: L.LeafletEvent) => {
                this._adjustClusterView(map);
            }, this);
        }
    }


    private _setFeatureLayerJson(geoJson: GeoJSON.FeatureCollection<GeoJSON.GeometryObject>, onLayerAdded: LayerAddedCallback, onClick?: (e: L.LeafletEvent) => void): void {
        if (geoJson === null) { throw new Error("geoJson can not be null."); }

        this._featureLayerJson.on("layeradd", onLayerAdded);
        this._featureLayerJson.setGeoJSON(geoJson);

        if (onClick) {
            this._featureLayerJson.on("click", onClick);
        }

        if (this._clusterPlugin === ClusterPlugin.MarkerCluster) {
            this._wellMarkerGroup = this._createWellMarkerCluster(this._featureLayerJson);
        } else if (this._clusterPlugin === ClusterPlugin.SuperCluster) {
            this._supercluster.load(geoJson.features);
        }
    }


    /**
     * Adds markers to the feature layer using an array of arrays.
     *
     * Internal array is structured as:
     * a[0]: Longitude
     * a[1]: Latitude
     * a[2]: Title
     * This is to follow the structure of GeoJSON's `coordinates` property.
     */
    private _setFeatureLayerArray(geoArray: any[][], onLayerAdded: LayerAddedCallback, onClick?: (e: L.LeafletEvent) => void): void {
        if (geoArray === null) { throw new Error("geoArray cannot be null."); }

        if (onClick) {
            this._featureLayerArray.on("click", onClick);
        }

        for (const point of geoArray){
            const title = point[2];
            const marker = L.marker(L.latLng(point[1], point[0]), {title});
            this._featureLayerArray.addLayer(marker);
        }
        if (this._clusterPlugin === ClusterPlugin.MarkerCluster) {
            this._wellMarkerGroup = this._createWellMarkerCluster(this._featureLayerArray);
        } else if (this._clusterPlugin === ClusterPlugin.SuperCluster) {
            this._supercluster.load(this._featureLayerArray.toGeoJSON().features);
        }
    }


    private _createWellMarkerCluster(layerGroup: L.mapbox.FeatureLayerT<WellGeoJSONLayer | L.Marker>): L.MarkerClusterGroup {
        const markerGroup = L.markerClusterGroup();
        const markerArray: (WellGeoJSONLayer | L.Marker)[] = [];
        layerGroup.eachLayer(function(marker): void {
            markerArray.push(marker);
        });
        markerGroup.addLayers(markerArray);

        return markerGroup;
    }


    private _createClusterIcon(feature: any, latLng: L.LatLng) : L.Marker {
        if (!feature.properties.cluster) {
            return L.marker(latLng);
        }

        const count = feature.properties.point_count;
        const size =
            count < 100 ? "small" :
            count < 1000 ? "medium" : "large";
        const icon = L.divIcon({
            html: `<div><span>${feature.properties.point_count_abbreviated}</span></div>`,
            className: `marker-cluster marker-cluster-${size}`,
            iconSize: L.point(40, 40)
        });
        return L.marker(latLng, { icon });
    }


    private _adjustClusterView(map: L.Map): void {
        const bounds = map.getBounds();
        const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
        const zoom = map.getZoom();
        this._wellSuperclusterGroup.clearLayers();
        this._wellSuperclusterGroup.addData(this._supercluster.getClusters(bbox, zoom));
    }


    private _clusterPlugin: ClusterPlugin;
    private _featureLayerArray: L.mapbox.FeatureLayerT<L.Marker>;
    private _featureLayerJson: L.mapbox.FeatureLayerT<WellGeoJSONLayer>;
    private _loadByArray: boolean;
    private _supercluster: any;
    private _wellMarkerGroup: L.MarkerClusterGroup | L.GeoJSON;
    private _wellSuperclusterGroup: L.GeoJSON;
}



// WEBPACK FOOTER //
// wellLayer.ts